/**
 * useSchemaDOM — Injects JSON-LD schemas via direct DOM manipulation.
 * Replaces react-helmet-async for <script type="application/ld+json"> which
 * was causing raw JSON text to render visibly on pages.
 * 
 * Includes sanitization to prevent Google Search Console parsing errors:
 * - Escapes "</script" sequences that break HTML parser
 * - Validates JSON output before injection
 */
import { useEffect } from 'react';

/**
 * Safely serialize a schema object for injection into a <script> tag.
 * Escapes sequences that would prematurely close the script block.
 */
function safeJsonStringify(obj: object): string | null {
  try {
    let json = JSON.stringify(obj);
    // Escape </ sequences that could break the HTML parser inside <script> tags
    // This is the #1 cause of "missing }" errors in Google Search Console
    json = json.replace(/<\//g, '<\\/');
    return json;
  } catch {
    console.warn('[useSchemaDOM] Failed to stringify schema:', obj);
    return null;
  }
}

export function useSchemaDOM(schemas: object[], id: string, deps: unknown[] = []) {
  useEffect(() => {
    if (!schemas.length) return;

    // Remove previous injections for this source id
    document.querySelectorAll(`script[data-seo="${id}"]`).forEach(el => el.remove());

    const hasExistingFAQPage = () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (const script of scripts) {
        try {
          const parsed = JSON.parse(script.textContent || '{}');
          const items = Array.isArray(parsed) ? parsed : [parsed];
          if (items.some(item => item && typeof item === 'object' && (item as { ['@type']?: string })['@type'] === 'FAQPage')) {
            return true;
          }
        } catch {
          // Ignore non-JSON-LD or malformed scripts
        }
      }
      return false;
    };

    let injectedFAQPage = false;

    for (const schema of schemas) {
      const schemaType = (schema as { ['@type']?: string })['@type'];
      const isFAQPage = schemaType === 'FAQPage';

      if (isFAQPage) {
        // Ensure exactly one FAQPage per page to avoid Google duplicate FAQPage errors
        if (injectedFAQPage || hasExistingFAQPage()) continue;
        injectedFAQPage = true;
      }

      const json = safeJsonStringify(schema);
      if (!json) continue; // Skip invalid schemas

      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.textContent = json;
      s.setAttribute('data-seo', id);
      document.head.appendChild(s);
    }

    return () => {
      document.querySelectorAll(`script[data-seo="${id}"]`).forEach(el => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
